import { SingleAssignment } from "@kolibrisoftware/customerportal-api";
import { useTranslation } from "@kolibrisoftware/hooks-util";
import { useTheme } from "injectors/theme";
import PlaceholderPhoto from "shared/ui/placeholder-thumbnail";
import { FC, useMemo } from "react";
import useStyles from "./styles";
import classnames from "classnames";
import { useSelector } from "store/helpers";
import { PageLoadingStatus } from "enums/page-loading-status";
import { getFormattedPrice } from "utils/numbers";

type Props = {
  assignment: SingleAssignment;
};

export const AssignmentCard: FC<Props> = ({ assignment }) => {
  const theme = useTheme();
  const styles = useStyles({ theme });
  const t = useTranslation();
  const culture = useSelector(state => state.main.culture);

  const homePhotos = useSelector(
    state => state.objectPreview.assignmentPreview.photos
  );

  const objectPreviewLoadingStatus = useSelector(
    state => state.objectPreview.loadingStatus
  );

  const translations = useMemo(
    () => ({
      price: assignment.forSale
        ? t("assignmentCard.price.forSale", {
            values: {
              price: getFormattedPrice(Number(assignment.price) || 0, culture),
            },
          })
        : t(`assignmentCard.price.forRent.${assignment.rentCondition}`, {
            values: {
              price: getFormattedPrice(Number(assignment.price) || 0, culture),
            },
          }),
    }),
    [t, assignment, culture]
  );

  const backgroundImage = useMemo(
    () => homePhotos?.[0]?.urlBig || assignment.previewThumbnailUrl || "",
    [homePhotos, assignment.previewThumbnailUrl]
  );

  const isObjectPreviewLoading = useMemo(
    () => objectPreviewLoadingStatus === PageLoadingStatus.Loading,
    [objectPreviewLoadingStatus]
  );

  return (
    <div className={styles.previewContainer}>
      <div className={styles.thumbWrapper}>
        <div className={styles.thumbContainer}>
          {backgroundImage ? (
            <div
              className={classnames(styles.thumb)}
              style={{
                backgroundImage: isObjectPreviewLoading
                  ? ""
                  : `url("${backgroundImage}")`,
              }}
            />
          ) : (
            <PlaceholderPhoto isResponsive={true} />
          )}
        </div>
      </div>
      <div className={styles.body}>
        <div className={styles.previewTitle}>{assignment.displayName}</div>
        <div className={styles.previewBody}>
          {`${assignment.postalCode} ${assignment.locality}`}
        </div>
        <div className={styles.previewBody}>{translations.price}</div>
      </div>
    </div>
  );
};
